import { ERROR_CODES, METHOD_STATUSES } from "@modules/sdk/constants";
import {
    checkRequiredArguments,
    checkSubscribers,
    promiseWithTimeout,
} from "@modules/sdk/helpers";
import SdkError from "@modules/sdk/methods/SdkError";
import SdkMethodsCollector from "@modules/sdk/methods/SdkMethodsCollector";
import type { SdkMethodsInterface } from "@modules/sdk/methods/SdkMethodsInterface";
import { SuccessResult } from "@modules/sdk/types";

import type { CustomerActiveRewardsResponseType } from "../modules/CustomerRewards/models/RewardsCustomerRewardsEntities";
import type { RewardsProgramAccountEntity } from "../modules/ProgramAccount/models/RewardsProgramAccountEntity";
import type { RewardsSpendingRulesRedeemBody } from "../modules/SpendingRules/types";
import type { RewardsVipTiersResponseType } from "../modules/VipTiers/models/RewardsVipTiersEntities";
import { ModalNames } from "../utils/modal/types";
import { REWARDS_SDK_METHODS } from "./constants";
import CloseRewardsPopupSdkMethodPayload from "./methods/CloseRewardsPopupSdkMethodPayload";
import GetCustomerActiveRewardsSdkMethodPayload from "./methods/GetCustomerActiveRewardsSdkMethodPayload";
import GetRewardsProgramAccountSdkMethodPayload from "./methods/GetRewardsProgramAccountSdkMethodPayload";
import GetRewardsSpendingRulesSdkMethodPayload from "./methods/GetRewardsSpendingRulesSdkMethodPayload";
import GetRewardsVipTiersSdkMethodPayload from "./methods/GetRewardsVipTiersSdkMethodPayload";
import OpenRewardsModalSdkMethodPayload from "./methods/OpenRewardsModalSdkMethodPayload";
import OpenRewardsPopupSdkMethodPayload from "./methods/OpenRewardsPopupSdkMethodPayload";
import RedeemRewardSdkMethodPayload from "./methods/RedeemRewardSdkMethodPayload";
import type {
    CustomerActiveRewardsSdkResultDTO,
    OpenRewardsModalSdkMethodParams,
    ProgramAccountSdkResultDTO,
    SpendingRulesSdkResultDTO,
    VipTiersSdkResultDTO,
} from "./types";
import { getSpendingRulesDto } from "./utils/getSpendingRulesDto";

class RewardsSdkMethods implements SdkMethodsInterface<REWARDS_SDK_METHODS> {
    constructor(private readonly _collector: SdkMethodsCollector) {}

    public openRewardsPopup() {
        return promiseWithTimeout(
            new Promise<SuccessResult>((resolve, reject) => {
                checkSubscribers(
                    REWARDS_SDK_METHODS.OPEN_REWARDS_POPUP,
                    reject
                );

                this._collector.notifySubscribers(
                    new OpenRewardsPopupSdkMethodPayload(() =>
                        resolve({ status: METHOD_STATUSES.SUCCESS })
                    )
                );
            })
        );
    }

    public closeRewardsPopup() {
        return promiseWithTimeout(
            new Promise<SuccessResult>((resolve, reject) => {
                checkSubscribers(
                    REWARDS_SDK_METHODS.CLOSE_REWARDS_POPUP,
                    reject
                );

                this._collector.notifySubscribers(
                    new CloseRewardsPopupSdkMethodPayload(() =>
                        resolve({ status: METHOD_STATUSES.SUCCESS })
                    )
                );
            })
        );
    }

    public getRewardsProgramAccount(locale: string) {
        return promiseWithTimeout(
            new Promise<SuccessResult<ProgramAccountSdkResultDTO>>(
                (resolve, reject) => {
                    checkSubscribers(
                        REWARDS_SDK_METHODS.GET_REWARDS_PROGRAM_ACCOUNT,
                        reject
                    );

                    this._collector.notifySubscribers(
                        new GetRewardsProgramAccountSdkMethodPayload(
                            locale,
                            (result: RewardsProgramAccountEntity) => {
                                const resultDTO: ProgramAccountSdkResultDTO = {
                                    points: result.points
                                        ? {
                                              balance: result.points.balance,
                                              expiresAt:
                                                  result.points.expiresAt,
                                          }
                                        : null,
                                    tier: result.tier?.currentTier
                                        ? {
                                              id: result.tier.currentTier.id,
                                              title: result.tier.currentTier
                                                  .title,
                                              achievedAt:
                                                  result.tier.currentTier
                                                      .achievedAt,
                                          }
                                        : null,
                                };

                                resolve({
                                    result: resultDTO,
                                    status: METHOD_STATUSES.SUCCESS,
                                });
                            },
                            () =>
                                reject(new SdkError(ERROR_CODES.GENERAL_ERROR))
                        )
                    );
                }
            )
        );
    }

    public getRewardsSpendingRules(locale: string) {
        return promiseWithTimeout(
            new Promise<SuccessResult<Array<SpendingRulesSdkResultDTO>>>(
                (resolve, reject) => {
                    checkSubscribers(
                        REWARDS_SDK_METHODS.GET_REWARDS_SPENDING_RULES,
                        reject
                    );

                    this._collector.notifySubscribers(
                        new GetRewardsSpendingRulesSdkMethodPayload(
                            locale,
                            (result) =>
                                resolve({
                                    result: getSpendingRulesDto(result.items),
                                    status: METHOD_STATUSES.SUCCESS,
                                }),
                            () =>
                                reject(new SdkError(ERROR_CODES.GENERAL_ERROR))
                        )
                    );
                }
            )
        );
    }

    public redeemReward(body: RewardsSpendingRulesRedeemBody) {
        return promiseWithTimeout(
            new Promise<SuccessResult>((resolve, reject) => {
                checkRequiredArguments(
                    REWARDS_SDK_METHODS.REDEEM_REWARD,
                    1,
                    arguments.length,
                    reject
                );

                checkSubscribers(REWARDS_SDK_METHODS.REDEEM_REWARD, reject);

                this._collector.notifySubscribers(
                    new RedeemRewardSdkMethodPayload(
                        body,
                        () => resolve({ status: METHOD_STATUSES.SUCCESS }),
                        () => reject(new SdkError(ERROR_CODES.GENERAL_ERROR))
                    )
                );
            })
        );
    }

    public getCustomerActiveRewards(locale: string) {
        return promiseWithTimeout(
            new Promise<
                SuccessResult<Array<CustomerActiveRewardsSdkResultDTO>>
            >((resolve, reject) => {
                checkSubscribers(
                    REWARDS_SDK_METHODS.GET_CUSTOMER_ACTIVE_REWARDS,
                    reject
                );

                this._collector.notifySubscribers(
                    new GetCustomerActiveRewardsSdkMethodPayload(
                        locale,
                        (result: CustomerActiveRewardsResponseType) => {
                            const resultDTO: Array<CustomerActiveRewardsSdkResultDTO> =
                                result.rewards.map((item) => ({
                                    id: item.id,
                                    type: item.type,
                                    reward: item.reward,
                                }));
                            resolve({
                                result: resultDTO,
                                status: METHOD_STATUSES.SUCCESS,
                            });
                        },
                        () => reject(new SdkError(ERROR_CODES.GENERAL_ERROR))
                    )
                );
            })
        );
    }

    public getRewardsVipTiers(locale: string) {
        return promiseWithTimeout(
            new Promise<SuccessResult<Array<VipTiersSdkResultDTO>>>(
                (resolve, reject) => {
                    checkSubscribers(
                        REWARDS_SDK_METHODS.GET_REWARDS_SPENDING_RULES,
                        reject
                    );

                    this._collector.notifySubscribers(
                        new GetRewardsVipTiersSdkMethodPayload(
                            locale,
                            (result: RewardsVipTiersResponseType) => {
                                const resultDTO: Array<VipTiersSdkResultDTO> =
                                    result.tiers.map((tier) => ({
                                        id: tier.id,
                                        title: tier.title,
                                        description: tier.description,
                                        perks: tier.perks,
                                        pointsMultiplier: tier.pointsMultiplier,
                                        image: tier.image,
                                        condition: tier.condition,
                                        conditionValue: tier.conditionValue,
                                        rewarding: tier.rewarding,
                                        isDefault: tier.isDefault,
                                    }));
                                resolve({
                                    result: resultDTO,
                                    status: METHOD_STATUSES.SUCCESS,
                                });
                            },
                            () =>
                                reject(new SdkError(ERROR_CODES.GENERAL_ERROR))
                        )
                    );
                }
            )
        );
    }

    public openRewardsModal(
        modalName: ModalNames,
        params: OpenRewardsModalSdkMethodParams
    ) {
        return promiseWithTimeout(
            new Promise<SuccessResult>((resolve, reject) => {
                checkSubscribers(
                    REWARDS_SDK_METHODS.OPEN_REWARDS_MODAL,
                    reject
                );

                this._collector.notifySubscribers(
                    new OpenRewardsModalSdkMethodPayload(
                        modalName,
                        params,
                        () =>
                            resolve({
                                result: params,
                                status: METHOD_STATUSES.SUCCESS,
                            }),
                        () =>
                            reject(
                                new SdkError(
                                    ERROR_CODES.GENERAL_ERROR,
                                    `Invalid modal name ${modalName}`
                                )
                            )
                    )
                );
            })
        );
    }
}

export default RewardsSdkMethods;
